import { FC, ReactNode } from 'react';
import { Avatar, Dropdown, Input, Menu } from 'antd';

import { useAntDesignTheme } from '@/hooks/useAntDesignTheme';
import { LearnMoreFooter } from '@/shared';
import { DropdownMenuPanel } from '@/shared/components/dropdowns/DropdownPanel';
import { getSchemaFieldIcon } from '@/shared/components/modals/ResourceActionModals/EditReportComponentModal/helpers/schemaFieldIcon';
import { useReportComponentStore } from '@/stores';
import { ReportComponent } from '@/types';
import { ReportComponentConfig } from '@/types/api/modules/business-intelligence';

import SelectOptionContent from '../../../../components/SelectOptionContent';
import { useDatasetSchema } from '../../../01-Overview/hooks';

interface MetricDropdownProps {
    component: ReportComponent;
    children: ReactNode;
    slot?: number;
    multiple?: boolean;
}

const MetricDropdown: FC<MetricDropdownProps> = ({ children, component, slot = 0, multiple = false }): JSX.Element => {
    const { token } = useAntDesignTheme();

    const updateDraft = useReportComponentStore((s) => s.updateDraftConfiguration);
    const draftConfigurations = useReportComponentStore((s) => s.draftConfigurations);

    const draft: Partial<ReportComponentConfig> = draftConfigurations[component?.id as string];

    const { schema } = useDatasetSchema(draft as ReportComponentConfig);

    const metrics = schema?.metrics ?? [];

    const menu = (
        <DropdownMenuPanel style={{ marginTop: '0.5rem', maxHeight: '25rem' }}>
            <div style={{ borderBottom: `1px solid ${token.colorBorder}` }}>
                <Input bordered={false} placeholder='Filter Options...' />
            </div>
            <div style={{ margin: '0.325rem 0', padding: '0 0.5rem' }}>
                <small style={{ color: token.colorTextTertiary }}>Metric Options</small>
            </div>
            {metrics.map((m: any) => {
                return (
                    <Menu.Item
                        key={m.key}
                        onClick={() => {
                            const currentMetrics = draft?.query?.metrics ?? [];
                            const metricsDraft = multiple
                                ? currentMetrics.some((metric) => metric.field === m.key)
                                    ? currentMetrics.filter((metric) => metric.field !== m.key)
                                    : [
                                          ...currentMetrics,
                                          {
                                              field: m?.key,
                                              label: m?.label,
                                              aggregation: 'sum' as const,
                                          },
                                      ]
                                : [...currentMetrics];

                            if (!multiple) {
                                metricsDraft[slot] = {
                                    field: m?.key,
                                    label: m?.label,
                                    aggregation: 'sum',
                                };
                            }

                            const updatedDraft = {
                                ...draft,
                                query: {
                                    ...draft?.query,
                                    metrics: metricsDraft,
                                },
                            } as Partial<ReportComponentConfig>;

                            updateDraft(component.id, updatedDraft);
                        }}
                    >
                        <SelectOptionContent
                            icon={
                                <Avatar
                                    size={16}
                                    shape='square'
                                    icon={getSchemaFieldIcon(m, token.colorTextTertiary, 'metric')}
                                    style={{ border: `1px solid ${token.colorBorder}` }}
                                />
                            }
                            title={m.label}
                            description={m.type}
                            selected={multiple && draft?.query?.metrics?.some((metric) => metric.field === m.key)}
                        />
                    </Menu.Item>
                );
            })}
            <LearnMoreFooter topic='charting functionality' variant='dropdown' />
        </DropdownMenuPanel>
    );

    return (
        <Dropdown trigger={['click']} popupRender={() => menu}>
            <span>{children}</span>
        </Dropdown>
    );
};

export default MetricDropdown;
